import moment from 'moment';
import create from 'zustand';
type FilterDto = {
  selectedSessionNames: string[];
  selectedCountries: string[];
  selectedStates: string[];
  selectedDistricts: string[];
  selectedBlocks: string[];
  selectedClusters: string[];
  selectedSchoolGroups: string[];
  selectedSchoolIds: string[];
  selectedGrades: string[];
};
const initialStates = {
  selectedSessionNames: [],
  selectedCountries: [],
  selectedStates: [],
  selectedDistricts: [],
  selectedBlocks: [],
  selectedClusters: [],
  selectedSchoolGroups: [],
  selectedSchoolIds: [],
  selectedGrades: [],
  sessionNameList: null,
  countryList: null,
  stateList: null,
  districtList: null,
  blockList: null,
  clusterList: null,
  schoolGroupList: null,
  schoolIdList: null,
  gradeList: null,
  startDate: null,
  endDate: null,
};
export type FilterState = {
  filterDto: FilterDto | null;
  selectedSessionNames: string[];
  selectedCountries: string[];
  selectedStates: string[];
  selectedDistricts: string[];
  selectedBlocks: string[];
  selectedClusters: string[];
  selectedSchoolGroups: string[];
  selectedSchoolIds: string[];
  selectedGrades: string[];
  sessionNameList: string[] | null;
  countryList: string[] | null;
  stateList: string[] | null;
  districtList: string[] | null;
  blockList: string[] | null;
  clusterList: string[] | null;
  schoolGroupList: string[] | null;
  schoolIdList: string[] | null;
  gradeList: string[] | null;
  startDate: any;
  endDate: any;
  setSelectedCountries: (countries: string[]) => void;
  setSelectedSessionNames: (names: string[]) => void;
  setSelectedStates: (states: string[]) => void;
  setSelectedDistricts: (districts: string[]) => void;
  setSelectedBlocks: (blocks: string[]) => void;
  setSelectedClusters: (clusters: string[]) => void;
  setSelectedSchoolGroups: (schoolGroups: string[]) => void;
  setSelectedSchoolIds: (schoolIds: string[]) => void;
  setSelectedGrades: (grades: string[]) => void;

  resetStore: () => void;
  setFilterDto: () => void;
  setSessionNameList: (list: string[]) => void;
  setCountryList: (list: string[]) => void;
  setStateList: (list: string[]) => void;
  setDistrictList: (list: string[]) => void;
  setBlockList: (list: string[]) => void;
  setClusterList: (list: string[]) => void;
  setSchoolGroupList: (list: string[]) => void;
  setSchoolIdList: (list: string[]) => void;
  setGradeList: (list: string[]) => void;
  setStartDate: (date: any) => void;
  setEndDate: (date: any) => void;
};

export const useFilterStore = create<FilterState>((set) => ({
  filterDto: null,
  ...initialStates,
  setSelectedSessionNames: (selectedSessionNames: string[]) =>
    set(() => {
      return {
        selectedSessionNames: selectedSessionNames,
        selectedCountries: [],
        selectedStates: [],
        selectedDistricts: [],
        selectedBlocks: [],
        selectedClusters: [],
        selectedSchoolGroups: [],
        selectedSchoolIds: [],
        selectedGrades: [],
      };
    }),
  setSelectedCountries: (selectedCountries: string[]) =>
    set(() => {
      return {
        selectedCountries: selectedCountries,
        selectedStates: [],
        selectedDistricts: [],
        selectedBlocks: [],
        selectedClusters: [],
        selectedSchoolGroups: [],
        selectedSchoolIds: [],
        selectedGrades: [],
      };
    }),
  setSelectedStates: (selectedStates: string[]) =>
    set(() => {
      return {
        selectedStates: selectedStates,
        selectedDistricts: [],
        selectedBlocks: [],
        selectedClusters: [],
        selectedSchoolGroups: [],
        selectedSchoolIds: [],
        selectedGrades: [],
      };
    }),
  setSelectedDistricts: (selectedDistricts: string[]) =>
    set(() => {
      return {
        selectedDistricts: selectedDistricts,
        selectedBlocks: [],
        selectedClusters: [],
        selectedSchoolGroups: [],
        selectedSchoolIds: [],
        selectedGrades: [],
      };
    }),
  setSelectedBlocks: (selectedBlocks: string[]) =>
    set(() => {
      return {
        selectedBlocks: selectedBlocks,
        selectedClusters: [],
        selectedSchoolGroups: [],
        selectedSchoolIds: [],
        selectedGrades: [],
      };
    }),
  setSelectedClusters: (selectedClusters: string[]) =>
    set(() => {
      return {
        selectedClusters: selectedClusters,
        selectedSchoolGroups: [],
        selectedSchoolIds: [],
        selectedGrades: [],
      };
    }),
  setSelectedSchoolGroups: (selectedSchools: string[]) =>
    set(() => {
      return {
        selectedSchoolGroups: selectedSchools,
        selectedSchoolIds: [],
        selectedGrades: [],
      };
    }),
  setSelectedSchoolIds: (selectedSchoolIds: string[]) =>
    set(() => {
      return {
        selectedSchoolIds: selectedSchoolIds,
        selectedGrades: [],
      };
    }),
  setSelectedGrades: (selectedGrades: string[]) =>
    set(() => {
      return {
        selectedGrades: selectedGrades,
      };
    }),
  resetStore: () =>
    set(() => {
      const todaysDate = moment(new Date(), 'DD/MM/YYYY');
      const oneYearOldDate = moment(
        new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
        'DD/MM/YYYY'
      );
      return {
        ...initialStates,
        filterDto: null,
        startDate: oneYearOldDate,
        endDate: todaysDate,
      };
    }),
  setFilterDto: () =>
    set((state) => {
      if (state.selectedSessionNames.length > 0) {
        return {
          filterDto: {
            selectedSessionNames: state.selectedSessionNames,
            selectedCountries: state.selectedCountries,
            selectedStates: state.selectedStates,
            selectedDistricts: state.selectedDistricts,
            selectedBlocks: state.selectedBlocks,
            selectedClusters: state.selectedClusters,
            selectedSchoolGroups: state.selectedSchoolGroups,
            selectedSchoolIds: state.selectedSchoolIds,
            selectedGrades: state.selectedGrades,
          },
        };
      }
      return {
        filterDto: null,
      };
    }),
  setSessionNameList: (list: string[]) =>
    set(() => {
      return {
        sessionNameList: list,
      };
    }),
  setCountryList: (list: string[]) =>
    set(() => {
      return {
        countryList: list,
      };
    }),
  setStateList: (list: string[]) =>
    set(() => {
      return {
        stateList: list,
      };
    }),
  setDistrictList: (list: string[]) =>
    set(() => {
      return {
        districtList: list,
      };
    }),
  setBlockList: (list: string[]) =>
    set(() => {
      return {
        blockList: list,
      };
    }),
  setClusterList: (list: string[]) =>
    set(() => {
      return {
        clusterList: list,
      };
    }),
  setSchoolGroupList: (list: string[]) =>
    set(() => {
      return {
        schoolGroupList: list,
      };
    }),
  setSchoolIdList: (list: string[]) =>
    set(() => {
      return {
        schoolIdList: list,
      };
    }),
  setGradeList: (list: string[]) =>
    set(() => {
      return {
        gradeList: list,
      };
    }),
  setStartDate: (date: any) =>
    set(() => {
      return {
        startDate: date,
      };
    }),
  setEndDate: (date: any) =>
    set(() => {
      return {
        endDate: date,
      };
    }),
}));
